import { useState, useEffect } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Badge } from "@/components/ui/badge";
import { analyteFormulasApi, analytesApi } from "@/lib/api";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, Calculator, Search, Info } from "lucide-react";

export default function AnalyteFormulas() {
  const [formulas, setFormulas] = useState([]);
  const [analytes, setAnalytes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [showDialog, setShowDialog] = useState(false);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [selectedFormula, setSelectedFormula] = useState(null);
  const [formData, setFormData] = useState({
    name: "",
    result_code: "",
    formula: "",
    description: "",
    active: true,
  });
  
  // Analyte search states
  const [analyteSearch, setAnalyteSearch] = useState("");
  const [showAnalyteDropdown, setShowAnalyteDropdown] = useState(false);
  const [highlightedIndex, setHighlightedIndex] = useState(0);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [formulasRes, analytesRes] = await Promise.all([
        analyteFormulasApi.getAll(),
        analytesApi.getAll(),
      ]);
      setFormulas(formulasRes.data);
      setAnalytes(analytesRes.data);
    } catch (error) {
      toast.error("Error al cargar datos");
    } finally {
      setLoading(false);
    }
  };

  const handleOpenDialog = (formula = null) => {
    if (formula) {
      setSelectedFormula(formula);
      setFormData({
        name: formula.name,
        result_code: formula.result_code,
        formula: formula.formula,
        description: formula.description || "",
        active: formula.active,
      });
    } else {
      setSelectedFormula(null);
      setFormData({
        name: "",
        result_code: "",
        formula: "",
        description: "",
        active: true,
      });
    }
    setAnalyteSearch("");
    setShowAnalyteDropdown(false);
    setHighlightedIndex(0);
    setShowDialog(true);
  };

  const handleSubmit = async () => {
    if (!formData.name || !formData.result_code || !formData.formula) {
      toast.error("Complete los campos requeridos");
      return;
    }

    try {
      if (selectedFormula) {
        await analyteFormulasApi.update(selectedFormula.id, formData);
        toast.success("Fórmula actualizada");
      } else {
        await analyteFormulasApi.create(formData);
        toast.success("Fórmula creada");
      }
      setShowDialog(false);
      fetchData();
    } catch (error) {
      toast.error("Error al guardar fórmula");
    }
  };

  const handleDelete = async () => {
    try {
      await analyteFormulasApi.delete(selectedFormula.id);
      toast.success("Fórmula eliminada");
      setShowDeleteDialog(false);
      setSelectedFormula(null);
      fetchData();
    } catch (error) {
      toast.error("Error al eliminar fórmula");
    }
  };

  const insertAnalyteCode = (code) => {
    setFormData((prev) => ({
      ...prev,
      formula: prev.formula + code,
    }));
  };

  const insertOperator = (op) => {
    setFormData((prev) => ({
      ...prev,
      formula: prev.formula + ` ${op} `,
    }));
  };

  const filteredFormulas = formulas.filter(
    (f) =>
      f.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      f.result_code.toLowerCase().includes(searchTerm.toLowerCase()) ||
      f.formula.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const filteredAnalytes = analytes.filter(
    (a) => a.code && a.code.trim() !== ""
  );

  // Filtered analytes for search dropdown
  const searchFilteredAnalytes = filteredAnalytes.filter(
    (a) =>
      analyteSearch === "" ||
      a.name.toLowerCase().includes(analyteSearch.toLowerCase()) ||
      a.code.toLowerCase().includes(analyteSearch.toLowerCase())
  );

  // Handle analyte search input
  const handleAnalyteSearchChange = (e) => {
    setAnalyteSearch(e.target.value);
    setShowAnalyteDropdown(true);
    setHighlightedIndex(0);
  };

  // Handle keyboard navigation
  const handleAnalyteKeyDown = (e) => {
    if (!showAnalyteDropdown || searchFilteredAnalytes.length === 0) {
      if (e.key === "ArrowDown" || e.key === "ArrowUp") {
        setShowAnalyteDropdown(true);
      }
      return;
    }

    switch (e.key) {
      case "ArrowDown":
        e.preventDefault();
        setHighlightedIndex((prev) =>
          prev < searchFilteredAnalytes.length - 1 ? prev + 1 : 0
        );
        break;
      case "ArrowUp":
        e.preventDefault();
        setHighlightedIndex((prev) =>
          prev > 0 ? prev - 1 : searchFilteredAnalytes.length - 1
        );
        break;
      case "Enter":
        e.preventDefault();
        if (searchFilteredAnalytes[highlightedIndex]) {
          insertAnalyteCode(searchFilteredAnalytes[highlightedIndex].code);
          setAnalyteSearch("");
          setShowAnalyteDropdown(false);
        }
        break;
      case "Escape":
        setShowAnalyteDropdown(false);
        break;
      default:
        break;
    }
  };

  // Select analyte from dropdown
  const selectAnalyte = (analyte) => {
    insertAnalyteCode(analyte.code);
    setAnalyteSearch("");
    setShowAnalyteDropdown(false);
    setHighlightedIndex(0);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="analyte-formulas-page">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold font-['Manrope'] tracking-tight">
            Cálculos de Analitos
          </h1>
          <p className="text-muted-foreground">
            Defina fórmulas para calcular valores de analitos automáticamente
          </p>
        </div>
        <Button onClick={() => handleOpenDialog()} data-testid="add-formula-btn">
          <Plus className="h-4 w-4 mr-2" />
          Nueva Fórmula
        </Button>
      </div>

      {/* Info Card */}
      <Card className="bg-blue-50 border-blue-200">
        <CardContent className="p-4">
          <div className="flex items-start gap-3">
            <Info className="h-5 w-5 text-blue-600 mt-0.5" />
            <div className="text-sm text-blue-800">
              <p className="font-medium mb-1">¿Cómo usar las fórmulas?</p>
              <p>
                Use los <strong>códigos de analitos</strong> como variables en sus fórmulas.
                Por ejemplo: <code className="bg-blue-100 px-1 rounded">BILT - BILD</code> calculará
                la resta de Bilirrubina Total menos Bilirrubina Directa.
              </p>
              <p className="mt-1">
                Operadores disponibles: <code className="bg-blue-100 px-1 rounded">+</code> (suma),{" "}
                <code className="bg-blue-100 px-1 rounded">-</code> (resta),{" "}
                <code className="bg-blue-100 px-1 rounded">*</code> (multiplicación),{" "}
                <code className="bg-blue-100 px-1 rounded">/</code> (división),{" "}
                <code className="bg-blue-100 px-1 rounded">( )</code> (paréntesis)
              </p>
            </div>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <div className="flex justify-between items-center">
            <CardTitle className="flex items-center gap-2 text-lg">
              <Calculator className="h-5 w-5" />
              Fórmulas Configuradas
            </CardTitle>
            <div className="relative w-64">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Buscar fórmula..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10"
              />
            </div>
          </div>
        </CardHeader>
        <CardContent>
          {filteredFormulas.length === 0 ? (
            <div className="text-center py-12 text-muted-foreground">
              <Calculator className="h-12 w-12 mx-auto mb-4 opacity-30" />
              <p>No hay fórmulas configuradas</p>
              <Button
                variant="link"
                onClick={() => handleOpenDialog()}
                className="mt-2"
              >
                Crear primera fórmula
              </Button>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Nombre</TableHead>
                  <TableHead>Código Resultado</TableHead>
                  <TableHead>Fórmula</TableHead>
                  <TableHead>Estado</TableHead>
                  <TableHead className="text-right">Acciones</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredFormulas.map((formula) => (
                  <TableRow key={formula.id}>
                    <TableCell className="font-medium">{formula.name}</TableCell>
                    <TableCell>
                      <Badge variant="outline" className="font-mono">
                        {formula.result_code}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      <code className="bg-muted px-2 py-1 rounded text-sm">
                        {formula.formula}
                      </code>
                    </TableCell>
                    <TableCell>
                      <Badge variant={formula.active ? "default" : "secondary"}>
                        {formula.active ? "Activo" : "Inactivo"}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="flex justify-end gap-2">
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => handleOpenDialog(formula)}
                        >
                          <Pencil className="h-4 w-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => {
                            setSelectedFormula(formula);
                            setShowDeleteDialog(true);
                          }}
                        >
                          <Trash2 className="h-4 w-4 text-destructive" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* Create/Edit Dialog */}
      <Dialog open={showDialog} onOpenChange={setShowDialog}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>
              {selectedFormula ? "Editar Fórmula" : "Nueva Fórmula"}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Nombre *</Label>
                <Input
                  placeholder="Ej: Bilirrubina Indirecta"
                  value={formData.name}
                  onChange={(e) =>
                    setFormData({ ...formData, name: e.target.value })
                  }
                />
              </div>
              <div className="space-y-2">
                <Label>Código del Resultado *</Label>
                <Input
                  placeholder="Ej: BILI"
                  value={formData.result_code}
                  onChange={(e) =>
                    setFormData({ ...formData, result_code: e.target.value.toUpperCase() })
                  }
                  className="font-mono"
                />
              </div>
            </div>

            {/* Analyte Search */}
            <div className="space-y-2">
              <Label>Buscar Analito</Label>
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input
                  placeholder="Escriba para buscar analitos por nombre o código..."
                  value={analyteSearch}
                  onChange={handleAnalyteSearchChange}
                  onKeyDown={handleAnalyteKeyDown}
                  onFocus={() => setShowAnalyteDropdown(true)}
                  className="pl-10"
                />
                {showAnalyteDropdown && searchFilteredAnalytes.length > 0 && (
                  <div className="absolute z-50 w-full mt-1 bg-background border rounded-lg shadow-lg max-h-48 overflow-auto">
                    {searchFilteredAnalytes.map((analyte, index) => (
                      <div
                        key={analyte.id}
                        className={`flex items-center justify-between px-3 py-2 cursor-pointer ${
                          index === highlightedIndex
                            ? "bg-primary text-primary-foreground"
                            : "hover:bg-muted"
                        }`}
                        onClick={() => selectAnalyte(analyte)}
                        onMouseEnter={() => setHighlightedIndex(index)}
                      >
                        <span className="text-sm">{analyte.name}</span>
                        <Badge variant={index === highlightedIndex ? "secondary" : "outline"} className="font-mono text-xs">
                          {analyte.code}
                        </Badge>
                      </div>
                    ))}
                  </div>
                )}
                {showAnalyteDropdown && analyteSearch && searchFilteredAnalytes.length === 0 && (
                  <div className="absolute z-50 w-full mt-1 bg-background border rounded-lg shadow-lg p-3 text-sm text-muted-foreground text-center">
                    No se encontraron analitos
                  </div>
                )}
              </div>
              <p className="text-xs text-muted-foreground">
                Use ↑↓ para navegar y Enter para seleccionar
              </p>
            </div>

            {/* Formula Field */}
            <div className="space-y-2">
              <Label>Fórmula *</Label>
              <div className="space-y-2">
                <Input
                  placeholder="Ej: BILT - BILD"
                  value={formData.formula}
                  onChange={(e) =>
                    setFormData({ ...formData, formula: e.target.value })
                  }
                  className="font-mono text-lg"
                />
                <div className="flex gap-1 flex-wrap">
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() => insertOperator("+")}
                  >
                    +
                  </Button>
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() => insertOperator("-")}
                  >
                    -
                  </Button>
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() => insertOperator("*")}
                  >
                    ×
                  </Button>
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() => insertOperator("/")}
                  >
                    ÷
                  </Button>
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() => insertOperator("(")}
                  >
                    (
                  </Button>
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() => insertOperator(")")}
                  >
                    )
                  </Button>
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    onClick={() => setFormData({ ...formData, formula: "" })}
                    className="text-destructive"
                  >
                    Limpiar
                  </Button>
                </div>
              </div>
            </div>

            <div className="space-y-2">
              <Label>Descripción</Label>
              <Textarea
                placeholder="Descripción opcional de la fórmula..."
                value={formData.description}
                onChange={(e) =>
                  setFormData({ ...formData, description: e.target.value })
                }
                rows={2}
              />
            </div>
            
            <div className="flex items-center gap-2">
              <Switch
                checked={formData.active}
                onCheckedChange={(checked) =>
                  setFormData({ ...formData, active: checked })
                }
              />
              <Label>Activo</Label>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowDialog(false)}>
              Cancelar
            </Button>
            <Button onClick={handleSubmit}>
              {selectedFormula ? "Guardar Cambios" : "Crear Fórmula"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation */}
      <AlertDialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>¿Eliminar fórmula?</AlertDialogTitle>
            <AlertDialogDescription>
              Esta acción no se puede deshacer. Se eliminará la fórmula "
              {selectedFormula?.name}".
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDelete}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              Eliminar
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
